/**
 * AI-created reports ("Create a report with AI", owner 2026-10-25). The AI only writes this
 * recipe (a ReportSpec); the browser computes every number from the same lists the other
 * reports use (reportEngine.ts), so numbers are exact and follow the person's access.
 * Same rules as the server's ReportSpecRules.cs (api/ai/report-spec, api/custom-reports).
 */

export type ReportSource = "time" | "work" | "invoices";

export type RangePreset =
  | "this_week"
  | "last_week"
  | "this_month"
  | "last_month"
  | "this_quarter"
  | "last_quarter"
  | "this_year"
  | "last_year"
  | "last_30_days"
  | "last_90_days"
  | "last_12_months"
  | "all_time";

export type Compare = "none" | "previous_period" | "same_period_last_year";

export type DateField =
  | "date"
  | "due"
  | "start"
  | "completed"
  | "invoice_date"
  | "due_date";

export type GroupBy =
  | "person"
  | "client"
  | "work_type"
  | "work"
  | "month"
  | "week"
  | "assignee"
  | "status";

export type SplitBy = "none" | "month" | "billable" | "status";

export type Metric =
  // time
  | "hours"
  | "billable_hours"
  | "non_billable_hours"
  | "unbilled_hours"
  | "value"
  | "unbilled_value"
  | "entries"
  | "avg_hours_per_entry"
  // work
  | "count"
  | "overdue"
  | "completed"
  | "completed_on_time_pct"
  | "avg_days_to_complete"
  | "budget_hours"
  | "tracked_hours"
  | "over_budget"
  // invoices (count is shared)
  | "total"
  | "paid"
  | "balance"
  | "overdue_balance"
  | "avg_invoice";

export type ChartKind = "bar" | "stacked_bar" | "line" | "none";

export type ReportFilters = {
  billable: "all" | "billable" | "non_billable";
  billed: "all" | "billed" | "unbilled";
  workStatus: "open" | "completed" | "overdue" | "any";
  invoiceStatus: "all" | "paid" | "unpaid" | "overdue";
};

export type ReportSpec = {
  version: 1;
  title: string;
  description: string;
  source: ReportSource;
  dateField: DateField;
  range: RangePreset;
  compare: Compare;
  filters: ReportFilters;
  groupBy: GroupBy;
  splitBy: SplitBy;
  metrics: Metric[];
  sort: { by: "label" | Metric; dir: "asc" | "desc" };
  limit: number;
  chart: ChartKind;
};

export type ValueKind = "hours" | "money" | "count" | "days" | "percent";

export const SOURCES: Record<
  ReportSource,
  {
    label: string;
    dateFields: DateField[];
    groupBy: GroupBy[];
    splitBy: SplitBy[];
    metrics: Metric[];
  }
> = {
  time: {
    label: "Time",
    dateFields: ["date"],
    groupBy: ["person", "client", "work_type", "work", "month", "week"],
    splitBy: ["none", "month", "billable"],
    metrics: [
      "hours",
      "billable_hours",
      "non_billable_hours",
      "unbilled_hours",
      "value",
      "unbilled_value",
      "entries",
      "avg_hours_per_entry",
    ],
  },
  work: {
    label: "Work",
    dateFields: ["due", "start", "completed"],
    groupBy: ["assignee", "client", "work_type", "status", "month"],
    splitBy: ["none", "month", "status"],
    metrics: [
      "count",
      "overdue",
      "completed",
      "completed_on_time_pct",
      "avg_days_to_complete",
      "budget_hours",
      "tracked_hours",
      "over_budget",
    ],
  },
  invoices: {
    label: "Invoices",
    dateFields: ["invoice_date", "due_date"],
    groupBy: ["client", "assignee", "month", "status"],
    splitBy: ["none", "month"],
    metrics: [
      "count",
      "total",
      "paid",
      "balance",
      "overdue_balance",
      "avg_invoice",
    ],
  },
};

export const METRIC_INFO: Record<Metric, { label: string; kind: ValueKind }> = {
  hours: { label: "Hours", kind: "hours" },
  billable_hours: { label: "Billable hours", kind: "hours" },
  non_billable_hours: { label: "Non-billable hours", kind: "hours" },
  unbilled_hours: { label: "Unbilled hours", kind: "hours" },
  value: { label: "Billable value", kind: "money" },
  unbilled_value: { label: "Unbilled value", kind: "money" },
  entries: { label: "Time entries", kind: "count" },
  avg_hours_per_entry: { label: "Hours per entry", kind: "hours" },
  count: { label: "Count", kind: "count" },
  overdue: { label: "Overdue", kind: "count" },
  completed: { label: "Completed", kind: "count" },
  completed_on_time_pct: { label: "Completed on time", kind: "percent" },
  avg_days_to_complete: { label: "Days to complete", kind: "days" },
  budget_hours: { label: "Budget hours", kind: "hours" },
  tracked_hours: { label: "Tracked hours", kind: "hours" },
  over_budget: { label: "Over budget", kind: "count" },
  total: { label: "Invoiced", kind: "money" },
  paid: { label: "Paid", kind: "money" },
  balance: { label: "Balance", kind: "money" },
  overdue_balance: { label: "Overdue balance", kind: "money" },
  avg_invoice: { label: "Average invoice", kind: "money" },
};

/** "Count" reads better with the source's noun. */
export const metricLabel = (metric: Metric, source: ReportSource) =>
  metric === "count"
    ? source === "invoices"
      ? "Invoices"
      : "Work items"
    : METRIC_INFO[metric].label;

export const GROUP_LABELS: Record<GroupBy, string> = {
  person: "Team member",
  assignee: "Team member",
  client: "Client",
  work_type: "Work type",
  work: "Work item",
  month: "Month",
  week: "Week",
  status: "Status",
};

export const RANGE_LABELS: Record<RangePreset, string> = {
  this_week: "This week",
  last_week: "Last week",
  this_month: "This month",
  last_month: "Last month",
  this_quarter: "This quarter",
  last_quarter: "Last quarter",
  this_year: "This year",
  last_year: "Last year",
  last_30_days: "Last 30 days",
  last_90_days: "Last 90 days",
  last_12_months: "Last 12 months",
  all_time: "All time",
};

export const COMPARE_LABELS: Record<Compare, string> = {
  none: "No comparison",
  previous_period: "Previous period",
  same_period_last_year: "Same period last year",
};

export const DATE_FIELD_LABELS: Record<DateField, string> = {
  date: "Date logged",
  due: "Due date",
  start: "Start date",
  completed: "Completion date",
  invoice_date: "Invoice date",
  due_date: "Due date",
};

const MONEY: Metric[] = [
  "value",
  "unbilled_value",
  "total",
  "paid",
  "balance",
  "overdue_balance",
  "avg_invoice",
];

/** True when the report shows money (needs Admin / "View Billings"). */
export const needsBilling = (spec: Pick<ReportSpec, "source" | "metrics">) =>
  spec.source === "invoices" || spec.metrics.some((m) => MONEY.includes(m));

export const isTimeBucket = (group: GroupBy) =>
  group === "month" || group === "week";

const DEFAULT_FILTERS: ReportFilters = {
  billable: "all",
  billed: "all",
  workStatus: "any",
  invoiceStatus: "all",
};

const pick = <T extends string>(value: unknown, allowed: T[], fallback: T) =>
  allowed.includes(value as T) ? (value as T) : fallback;

const RANGES = Object.keys(RANGE_LABELS) as RangePreset[];

/**
 * Cleans a spec from the server / a saved report so the engine can always run it
 * (same fixes as the server). Returns null when it can't be a report at all.
 */
export const normalizeSpec = (raw: unknown): ReportSpec | null => {
  if (!raw || typeof raw !== "object") return null;
  const r = raw as Record<string, unknown>;
  const source = r.source as ReportSource;
  const info = SOURCES[source];
  if (!info) return null;
  const metrics = (Array.isArray(r.metrics) ? r.metrics : [])
    .filter(
      (m, i, all): m is Metric =>
        info.metrics.includes(m as Metric) && all.indexOf(m) === i,
    )
    .slice(0, 4);
  if (!metrics.length) return null;
  const groupBy = info.groupBy.includes(r.groupBy as GroupBy)
    ? (r.groupBy as GroupBy)
    : null;
  if (!groupBy) return null;

  const range = pick(r.range, RANGES, "this_year");
  let dateField = pick(r.dateField, info.dateFields, info.dateFields[0]);
  const f = (r.filters || {}) as Record<string, unknown>;
  const filters: ReportFilters = { ...DEFAULT_FILTERS };
  if (source === "time") {
    filters.billable = pick(
      f.billable,
      ["all", "billable", "non_billable"],
      "all",
    );
    filters.billed = pick(f.billed, ["all", "billed", "unbilled"], "all");
  }
  if (source === "work") {
    filters.workStatus = pick(
      f.workStatus,
      ["open", "completed", "overdue", "any"],
      "any",
    );
    if (dateField === "completed") filters.workStatus = "completed";
    if (filters.workStatus === "completed" && dateField === "due")
      dateField = "completed";
  }
  if (source === "invoices")
    filters.invoiceStatus = pick(
      f.invoiceStatus,
      ["all", "paid", "unpaid", "overdue"],
      "all",
    );

  let splitBy = pick(r.splitBy, info.splitBy, "none");
  if (splitBy === groupBy || (splitBy === "month" && isTimeBucket(groupBy)))
    splitBy = "none";
  let compare = pick(
    r.compare,
    ["none", "previous_period", "same_period_last_year"],
    "none",
  );
  if (range === "all_time") compare = "none";

  const s = (r.sort || {}) as Record<string, unknown>;
  const sortBy: "label" | Metric =
    s.by === "label" || metrics.includes(s.by as Metric)
      ? (s.by as "label" | Metric)
      : isTimeBucket(groupBy)
      ? "label"
      : metrics[0];
  const dir = pick(s.dir, ["asc", "desc"], sortBy === "label" ? "asc" : "desc");

  let chart = pick(r.chart, ["bar", "stacked_bar", "line", "none"], "bar");
  if (chart === "line" && !isTimeBucket(groupBy)) chart = "bar";
  if (chart === "stacked_bar" && splitBy === "none") chart = "bar";

  const limit = Math.min(100, Math.max(5, Math.round(Number(r.limit) || 25)));
  const title =
    String(r.title || "")
      .trim()
      .slice(0, 80) || "Custom report";
  return {
    version: 1,
    title,
    description: String(r.description || "")
      .trim()
      .slice(0, 200),
    source,
    dateField,
    range,
    compare,
    filters,
    groupBy,
    splitBy,
    metrics,
    sort: { by: sortBy, dir },
    limit,
    chart,
  };
};

/** Team member / client / work type filters saved with an AI-made report (`spec.view`, 2026-10-26). */
export type ReportViewFilters = {
  userIds: string[];
  clientIds: string[];
  workTypeIds: string[];
};

const viewIds = (raw: unknown) =>
  (Array.isArray(raw) ? raw : [])
    .map((x) => String(x ?? "").trim())
    .filter((x, i, all) => x && x.length <= 64 && all.indexOf(x) === i)
    .slice(0, 200);

/** The saved filters of a stored spec (empty lists when there are none). */
export const normalizeView = (raw: unknown): ReportViewFilters => {
  const v = ((raw as Record<string, unknown> | null)?.view || {}) as Record<
    string,
    unknown
  >;
  return {
    userIds: viewIds(v.userIds),
    clientIds: viewIds(v.clientIds),
    workTypeIds: viewIds(v.workTypeIds),
  };
};

/** One line under the title: what the report counts ("How it's calculated"). */
export const describeSpec = (spec: ReportSpec): string => {
  const parts: string[] = [];
  if (spec.source === "time") {
    const kind =
      spec.filters.billed === "unbilled"
        ? "billable time not invoiced yet"
        : spec.filters.billed === "billed"
        ? "invoiced time"
        : spec.filters.billable === "billable"
        ? "billable time"
        : spec.filters.billable === "non_billable"
        ? "non-billable time"
        : "all logged time";
    parts.push(`Counts ${kind}`);
    if (spec.metrics.some((m) => m === "value" || m === "unbilled_value"))
      parts.push("value = hours × each person's current hourly rate");
  } else if (spec.source === "work") {
    const kind =
      spec.filters.workStatus === "open"
        ? "open work items"
        : spec.filters.workStatus === "completed"
        ? "completed work items"
        : spec.filters.workStatus === "overdue"
        ? "overdue work items"
        : "work items (any status)";
    parts.push(`Counts ${kind}, not archived`);
    if (spec.dateField === "completed" || spec.metrics.includes("completed"))
      parts.push("completion date = when the status last changed");
  } else {
    const kind =
      spec.filters.invoiceStatus === "all"
        ? "all invoices"
        : `${spec.filters.invoiceStatus} invoices`;
    parts.push(`Counts ${kind}`);
    if (spec.metrics.includes("paid")) parts.push("paid = total − balance");
  }
  parts.push(
    `${DATE_FIELD_LABELS[spec.dateField].toLowerCase()} in ${RANGE_LABELS[
      spec.range
    ].toLowerCase()}`,
  );
  return parts.join(" · ");
};
